import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import { FreightCondition } from '@retail/calculus/types';
import { CalculusInfoRow } from './CalculusInfoRow';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { CalculusAreaAccordionDetails } from './CalculusAreaAccordionDetails';
import { useMemo } from 'react';
import { formatPrice } from '@retail/products/utils';
import { useTranslation } from 'react-i18next';

interface Props {
  freightCondition?: FreightCondition;
  costPrice?: number;
}

export function FreightConditionAccordion({ freightCondition, costPrice }: Props) {
  const { i18n } = useTranslation();
  const t = useCalculusTFunction();

  const freightLabel = useMemo(() => {
    if (freightCondition?.freightPercentAddon) {
      return `${formatPrice(freightCondition.freightPercentAddon, i18n.language)}%`;
    } else if (freightCondition?.freightFixedAddOn) {
      return t('calculus.pricePerUnit', {
        price: freightCondition.freightFixedAddOn,
        unit: freightCondition.freightFixedAddOnUnit,
      });
    }
    return '-';
  }, [
    freightCondition?.freightFixedAddOn,
    freightCondition?.freightFixedAddOnUnit,
    freightCondition?.freightPercentAddon,
    i18n.language,
    t,
  ]);

  return (
    <Accordion>
      <AccordionSummary>
        <CalculusInfoRow
          description={t('calculus.prices.costPrice')}
          price={costPrice}
          condition={
            <Typography pl={1} color="text.secondary" variant="body2">
              {freightLabel}
            </Typography>
          }
        />
      </AccordionSummary>
      <AccordionDetails>
        {freightCondition ? (
          <CalculusAreaAccordionDetails
            bindingType={freightCondition.bindingType}
            validFrom={freightCondition.validFrom}
            validTo={freightCondition.validTo}
          />
        ) : (
          <Typography variant="body2" sx={{ pl: 2 }}>
            {t('calculus.noFreightCondition')}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
